/** Shared display and capture identity; a business filter never supplies a stamp. */
import api from "@/lib/api";
import { SCHEDULER_VIEW_ENABLED } from "@/lib/featureFlags";
import { addDays, isoDate } from "@/lib/schedule";

export function occurrenceKey(occ) {
  return occ.occurrence_id || `${occ.slot_id}-${occ.date}`;
}

export function selectionFromOccurrence(occ) {
  if (occ.scheduler_appointment_id) return {
    connectionId: occ.scheduler_connection_id, appointmentId: occ.scheduler_appointment_id,
    revision: occ.scheduler_revision, contextLabel: occ.context_label || "Unassigned",
    participantsRevision: occ.scheduler_participants_revision,
    classSession: occ.class_session, attendanceReviewable: occ.attendance_reviewable,
    studentIds: occ.student_ids, classId: occ.class_id, studentId: occ.student_id,
    consentReady: occ.consent_ready,
    lessonDate: occ.lesson_date || occ.date, date: occ.date,
  };
  return { slotId: occ.slot_id, date: occ.date, contextLabel: occ.context_label,
    studentId: occ.student_id, classId: occ.class_id };
}

export function selectionFromParams(params, legacyKey = "slotId") {
  const connectionId = params.get("connectionId"), appointmentId = params.get("appointmentId");
  if (connectionId || appointmentId || params.has("revision")) return {
    connectionId, appointmentId, revision: Number(params.get("revision")),
    participantsRevision: params.get("participantsRevision") || undefined,
  };
  const slotId = params.get(legacyKey), date = params.get("date");
  return slotId && date ? { slotId, date } : null;
}

export function selectionParams(selection, legacyKey = "slotId") {
  if (selection?.appointmentId) return new URLSearchParams({ connectionId: selection.connectionId,
    appointmentId: selection.appointmentId, revision: String(selection.revision),
    ...(selection.participantsRevision ? { participantsRevision: selection.participantsRevision } : {}) });
  return new URLSearchParams(selection?.slotId ? { [legacyKey]: selection.slotId, date: selection.date } : {});
}

export function selectionPayload(selection) {
  if (!selection) return {};
  if (selection.appointmentId || selection.connectionId || selection.revision !== undefined) {
    if (!selection.connectionId || !selection.appointmentId || !Number.isSafeInteger(selection.revision) || selection.revision < 1) {
      throw new Error("Refresh the selected appointment before recording.");
    }
    return { scheduler_connection_id: selection.connectionId, scheduler_appointment_id: selection.appointmentId,
      scheduler_revision: selection.revision,
      ...(selection.participantsRevision ? { scheduler_participants_revision: selection.participantsRevision } : {}) };
  }
  return selection.slotId && selection.date ? { scheduled_slot_id: selection.slotId, scheduled_date: selection.date } : {};
}

export function sameSelection(selected, occ) {
  return selected?.appointmentId ? selected.connectionId === occ.scheduler_connection_id && selected.appointmentId === occ.scheduler_appointment_id
    : !!selected?.slotId && selected.slotId === occ.slot_id && selected.date === occ.date;
}

export async function teachingView(from, to, options = {}) {
  if (!SCHEDULER_VIEW_ENABLED) return (await api.get(`/schedule?from=${from}&to=${to}`, options)).data;
  return (await api.get("/scheduler/teaching-view", { ...options, params: { from, to, ...options.params } })).data;
}

export async function teachingToday(options = {}) {
  if (!SCHEDULER_VIEW_ENABLED) return (await api.get("/schedule/today", options)).data;
  return (await api.get("/scheduler/teaching-today", options)).data;
}

/**
 * The sessions ahead, for Home's next lesson or class once today is done:
 * the next 30 days of the teaching view, or four weeks of the legacy schedule.
 */
export async function teachingNext(options = {}) {
  if (!SCHEDULER_VIEW_ENABLED) {
    const from = new Date();
    return (await api.get(`/schedule?from=${isoDate(from)}&to=${isoDate(addDays(from, 28))}`, options)).data;
  }
  return (await api.get("/scheduler/teaching-next", options)).data;
}

export async function verifySelection(selection, target, options = {}) {
  if (!selection || (!selection.appointmentId && !selection.connectionId && selection.revision === undefined)) return selection;
  selectionPayload(selection);
  const { purpose, ...requestOptions } = options;
  const { data } = await api.get(`/scheduler/connections/${encodeURIComponent(selection.connectionId)}/appointments/${encodeURIComponent(selection.appointmentId)}/selection`, requestOptions);
  const allowed = purpose === 'notes' ? (data.notes_writable ?? data.recordable) : (data.recordable || data.attendance_reviewable);
  if (!allowed || data.scheduler_connection_id !== selection.connectionId || data.scheduler_appointment_id !== selection.appointmentId || data.scheduler_revision !== selection.revision ||
      (selection.participantsRevision && data.scheduler_participants_revision !== selection.participantsRevision) ||
      (target.classId ? data.class_id !== target.classId : data.student_id !== target.studentId || data.class_id)) {
    throw new Error("The appointment or its students changed. Refresh the lesson selection.");
  }
  return selectionFromOccurrence(data);
}
